/**
 * Pomijanie sekcji poza ekranem (`content-visibility: auto`).
 *
 * Strona główna ma ~10 500 px wysokości, a przeglądarka domyślnie przelicza styl, układ
 * i malowanie dla całej strony, nie tylko dla widocznego fragmentu. Zmierzone przeliczenie
 * stylu drzewa: 61 ms bez pomijania, 15-18 ms z pomijaniem.
 *
 * Dlaczego skryptem, a nie samym CSS-em: `content-visibility: auto` wymaga podania wysokości,
 * jaką sekcja ma zajmować zanim zostanie policzona (`contain-intrinsic-size`). W CSS trzeba by
 * ją zgadnąć, a sekcje mają od 72 do 2147 px — przy wspólnym oszacowaniu wysokość strony
 * zmieniała się w trakcie przewijania (10 836 → 9 954 px) i pasek przewijania skakał.
 * Tutaj mierzymy każdą sekcję po wyrenderowaniu, więc oszacowanie jest dokładne co do piksela
 * i wysokość strony się nie rusza.
 *
 * Słowo `auto` w `contain-intrinsic-size` sprawia dodatkowo, że po pierwszym wyrenderowaniu
 * przeglądarka zapamiętuje rzeczywisty rozmiar i sama koryguje ewentualne różnice.
 */

/** Ile pierwszych sekcji zostawiamy nietknięte — są nad zgięciem i muszą wyrenderować się od razu. */
const SKIP_FIRST = 2;

/**
 * Elementy przyklejone (`position: sticky`). `content-visibility` wymusza `contain: paint`,
 * co przycina zawartość do pudełka sekcji i zepsułoby przyklejanie.
 */
const STICKY_SELECTOR = '.sticky-content--desktop, sticky-add-to-cart, .product-information';

/** @type {HTMLElement[]} */
let sections = [];

/** Zdejmuje pomijanie, żeby dało się zmierzyć prawdziwą wysokość. */
function reset() {
  for (const section of sections) {
    section.style.contentVisibility = '';
    section.style.containIntrinsicSize = '';
  }
}

/**
 * Mierzy wszystkie sekcje, a dopiero potem zapisuje wyniki — odczyty i zapisy są rozdzielone,
 * żeby nie wymuszać przeliczania układu przy każdej sekcji z osobna.
 */
function apply() {
  const heights = sections.map((section) => section.offsetHeight);

  sections.forEach((section, index) => {
    const height = heights[index];
    if (!height) return;

    section.style.containIntrinsicSize = `auto ${height}px`;
    section.style.contentVisibility = 'auto';
  });
}

function refresh() {
  reset();
  // Pomiar w następnej klatce, już po tym jak zdjęcie pomijania zostanie uwzględnione w układzie.
  requestAnimationFrame(apply);
}

function init() {
  const main = document.querySelector('.content-for-layout');
  if (!main || !('contentVisibility' in document.documentElement.style)) return;

  sections = /** @type {HTMLElement[]} */ (
    Array.from(main.children).filter(
      (child, index) =>
        index >= SKIP_FIRST &&
        child instanceof HTMLElement &&
        child.classList.contains('shopify-section') &&
        !child.querySelector(STICKY_SELECTOR)
    )
  );

  if (!sections.length) return;

  apply();

  // Zmiana szerokości okna zmienia wysokości sekcji, więc zmierzone wartości trzeba odświeżyć.
  let resizeTimer = 0;
  let lastWidth = window.innerWidth;
  window.addEventListener(
    'resize',
    () => {
      if (window.innerWidth === lastWidth) return;
      lastWidth = window.innerWidth;
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(refresh, 250);
    },
    { passive: true }
  );

  // Edytor motywu podmienia sekcje w locie — po każdej podmianie mierzymy od nowa.
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', refresh);
    document.addEventListener('shopify:section:unload', refresh);
  }
}

// Czekamy na pełne załadowanie: obrazy i czcionki wpływają na wysokość sekcji, a mierzymy raz.
if (document.readyState === 'complete') {
  init();
} else {
  window.addEventListener('load', init, { once: true });
}
